import { useEffect, useState, useCallback } from 'react'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Squad from './pages/Squad'
import Fixtures from './pages/Fixtures'
import Tactics from './pages/Tactics'
import Match from './pages/Match'
import Finance from './pages/Finance'
import Transfer from './pages/Transfer'
import Stock from './pages/Stock'
import Banking from './pages/Banking'
import Cup from './pages/Cup'
import SetPiece from './pages/SetPiece'
import News from './pages/News'
import Onboarding from './pages/Onboarding'
import NavBar, { Page } from './components/NavBar'
import { AuthState, clearAuth, loadAuth } from './store/auth'
import { ClubInfo, ClubLoadResponse, FixtureRow } from './store/club'
import { apiClubLoad, apiUserLogout } from './server/ServiceCall'

function App() {
  const [auth,       setAuth]      = useState<AuthState | null>(null)
  const [page,       setPage]      = useState<Page>('dashboard')
  const [club,       setClub]      = useState<ClubInfo | null>(null)
  const [nextMatch,  setNextMatch] = useState<FixtureRow | null>(null)
  const [recent,     setRecent]    = useState<FixtureRow[]>([])
  const [hasClub,    setHasClub]   = useState<'?' | 'Y' | 'N'>('?')
  const [activeFixture, setActiveFixture] = useState<number | null>(null)
  const [pageNonce,     setPageNonce]     = useState(0)

  const reloadClub = useCallback(async (token: string) => {
    const r = await apiClubLoad(token)
    if (r.resultCode !== '00') { setHasClub('?'); return }
    try {
      const obj = JSON.parse(r.resultData) as ClubLoadResponse
      setHasClub(obj.hasClub)
      setClub(obj.club)
      setNextMatch(obj.nextMatch)
      setRecent(obj.recent ?? [])
    } catch { setHasClub('?') }
  }, [])

  useEffect(() => {
    const a = loadAuth()
    setAuth(a)
    if (a) reloadClub(a.token)

    function onSessionExpired() {
      clearAuth()
      setAuth(null)
      setHasClub('?')
      setClub(null)
      setNextMatch(null)
      setRecent([])
    }
    window.addEventListener('session-expired', onSessionExpired)
    return () => window.removeEventListener('session-expired', onSessionExpired)
  }, [reloadClub])

  // 페이지 전환마다 ClubLoad 새로고침 + 자식 페이지 강제 재조회 nonce
  useEffect(() => {
    if (auth) reloadClub(auth.token)
    setPageNonce(n => n + 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  function handleLoggedIn(a: AuthState) {
    setAuth(a)
    setPage('dashboard')
    reloadClub(a.token)
  }

  async function handleLogout() {
    if (auth) {
      try { await apiUserLogout(auth.token) } catch { /* ignore */ }
    }
    clearAuth()
    setAuth(null)
    setHasClub('?')
    setClub(null)
    setNextMatch(null)
    setRecent([])
  }

  if (!auth) return <Login onLoggedIn={handleLoggedIn} />

  if (hasClub === '?') return <div className="page-pad">로딩 중…</div>

  if (hasClub === 'N' || !club) {
    return (
      <Onboarding
        auth={auth}
        onCreated={() => reloadClub(auth.token)}
        onLoggedOut={handleLogout}
      />
    )
  }

  return (
    <div className="app-shell">
      <NavBar
        current={page}
        onChange={setPage}
        auth={auth}
        clubName={club.CLUB_NAME}
        cash={club.CASH}
        onLogout={handleLogout}
      />
      {page === 'dashboard' && (
        <Dashboard
          auth={auth}
          club={club}
          nextMatch={nextMatch}
          recent={recent}
          onGoSquad={() => setPage('squad')}
          onGoFixtures={() => setPage('fixtures')}
          onSeasonEnded={() => reloadClub(auth.token)}
        />
      )}
      {page === 'squad' && (
        <Squad auth={auth} clubCode={club.CLUB_CODE} canViewPotential={club.USER_IS_OWNER === 1} />
      )}
      {page === 'fixtures' && (
        <Fixtures
          auth={auth}
          leagueCode={club.LEAGUE_CODE}
          seasonNo={club.SEASON_NO}
          myClubCode={club.CLUB_CODE}
          roundTotal={club.ROUND_TOTAL || 46}
          onGoMatch={(fc) => { setActiveFixture(fc); setPage('match') }}
        />
      )}
      {page === 'tactics' && (
        <Tactics auth={auth} clubCode={club.CLUB_CODE} />
      )}
      {page === 'finance' && (
        <Finance auth={auth} clubCode={club.CLUB_CODE} seasonNo={club.SEASON_NO} nonce={pageNonce} />
      )}
      {page === 'transfer' && (
        <Transfer auth={auth} leagueCode={club.LEAGUE_CODE} myClubCode={club.CLUB_CODE} myCash={club.CASH}
                  canViewPotential={club.USER_IS_OWNER === 1} nonce={pageNonce} />
      )}
      {page === 'stock' && (
        <Stock auth={auth} myCash={club.CASH} nonce={pageNonce} />
      )}
      {page === 'banking' && (
        <Banking auth={auth} clubCode={club.CLUB_CODE} seasonNo={club.SEASON_NO} nonce={pageNonce} />
      )}
      {page === 'cup' && (
        <Cup auth={auth} tier={club.TIER || 8} championsQualified={club.CHAMPIONS_QUALIFIED} nonce={pageNonce} />
      )}
      {page === 'setpiece' && (
        <SetPiece auth={auth} leagueCode={club.LEAGUE_CODE} seasonNo={club.SEASON_NO} nonce={pageNonce} />
      )}
      {page === 'news' && (
        <News auth={auth} nonce={pageNonce} />
      )}
      {page === 'match' && activeFixture != null && (
        <Match auth={auth} fixtureCode={activeFixture} myClubCode={club.CLUB_CODE}
               onBack={() => { setActiveFixture(null); setPage('fixtures'); reloadClub(auth.token) }} />
      )}
    </div>
  )
}

export default App
